/**
 * Integração com o widget de chat do Movidesk (suporte).
 *
 * Contrato do fornecedor (chat-widget.min.js):
 * - `window.mdChatClient` precisa existir ANTES do script rodar — é o ID do aplicativo de chat;
 * - `window.movideskChatWidget.login({...})` identifica quem está falando (envolve a função global
 *   `movideskLogin` em try/catch);
 * - `show()`/`hide()` alternam a `visibility` do elemento `#md-app-widget`.
 *
 * Não existe campo livre no widget: os únicos dados que chegam ao agente são name, email,
 * codeReference e organizationCodeReference. Por isso a organização vai embutida no `name`.
 */

// ID do aplicativo de chat no Movidesk. Não é segredo (fica visível no HTML de qualquer site que
// use o widget) e é o mesmo para todos os tenants — por isso constante, e não variável de ambiente:
// um .env esquecido no deploy deixaria o suporte fora do ar sem aviso.
const ID_APLICATIVO = 'A969FCBB8B0F463AA1B8F682E59742F5'

const URL_SCRIPT = 'https://chat.movidesk.com/Scripts/chat-widget.min.js'

// Elemento que o widget cria no body; é nele que o Movidesk manda mexer para mostrar/ocultar.
const ID_ELEMENTO_WIDGET = 'md-app-widget'

// O elemento e o objeto global só existem alguns instantes depois do onload do script (o widget
// monta o iframe de forma assíncrona). Sem a espera, o primeiro hide() da sessão erraria o alvo e
// o chat apareceria na área administrativa até a próxima navegação.
const INTERVALO_ESPERA_MS = 200
const TENTATIVAS_ESPERA = 50

export type DadosLoginChat = {
  name: string
  email: string
  organizationCodeReference?: string
  stayConnected: boolean
  emptySubject: boolean
  startChat: boolean
}

declare global {
  interface Window {
    mdChatClient?: string
    movideskChatWidget?: {
      show: () => void
      hide: () => void
      maximize: () => void
      minimize: () => void
      login: (dados: DadosLoginChat) => void
    }
  }
}

// Uma carga por sessão de página: o script é global (mexe em window e no body) e não tem como ser
// descarregado. A promise guardada faz o segundo chamador esperar a primeira carga em vez de
// injetar outra tag — o que cobre o duplo mount do StrictMode e as poucas transições que de fato
// remontam o LayoutPage (seleção de organização, login e logout; entre telas do Cartivia o React
// reconcilia no lugar, porque todas as rotas usam o mesmo wrapper).
let carregamento: Promise<boolean> | null = null

// Sem gate de ambiente, por decisão: o chat roda em TODOS os hostnames, localhost incluído.
// ⚠️ Consequência a ter em mente ao testar: a fila do Movidesk é a de produção, então uma conversa
// aberta em desenvolvimento ou homologação vira chamado de verdade, com o nome e o e-mail de quem
// estiver testando. Não há como distinguir ambiente do lado do provedor (o ID do aplicativo é um só).
const injetarScript = (): Promise<boolean> =>
  new Promise(resolve => {
    window.mdChatClient = ID_APLICATIVO
    const script = document.createElement('script')
    script.src = URL_SCRIPT
    script.async = true
    script.onload = () => resolve(true)
    // Bloqueio de rede/adblock não pode derrubar a tela: o chat simplesmente não aparece.
    script.onerror = () => resolve(false)
    document.body.appendChild(script)
  })

export const carregarChatMovidesk = (): Promise<boolean> => {
  if (carregamento === null) {
    carregamento = injetarScript()
  }
  return carregamento
}

/** O widget já foi pedido nesta sessão de página? Usado para não injetar o script à toa. */
export const chatFoiCarregado = () => carregamento !== null

/**
 * Espera algo que o script do fornecedor cria de forma assíncrona. Devolve `null` ao estourar o
 * orçamento — o chamador decide o que fazer com a desistência, mas ela nunca é silenciosa.
 */
const esperar = <T>(obter: () => T | null | undefined): Promise<T | null> =>
  new Promise(resolve => {
    let restantes = TENTATIVAS_ESPERA
    const tentar = () => {
      const valor = obter()
      if (valor) {
        resolve(valor)
        return
      }
      restantes -= 1
      if (restantes <= 0) {
        resolve(null)
        return
      }
      window.setTimeout(tentar, INTERVALO_ESPERA_MS)
    }
    tentar()
  })

/**
 * Mostra ou oculta o widget. Usa a API do fornecedor **e** o elemento direto (que é o que ela faz
 * por dentro): o wrapper engole a própria falha num `console.error`, e ocultar o chat na área
 * administrativa é requisito — não pode depender de um caminho só.
 */
export const definirVisibilidadeChat = async (visivel: boolean) => {
  const elemento = await esperar(() =>
    document.getElementById(ID_ELEMENTO_WIDGET)
  )
  if (visivel) {
    window.movideskChatWidget?.show()
  } else {
    window.movideskChatWidget?.hide()
  }
  if (!elemento) {
    // Só chega aqui se o fornecedor tiver trocado o id do elemento ou a carga tiver passado do
    // orçamento de espera. Avisa em vez de desistir calado: o modo de falha ruim é o widget
    // continuar visível numa tela onde ele não deveria aparecer.
    // eslint-disable-next-line no-console
    console.warn(
      `Chat de suporte: elemento #${ID_ELEMENTO_WIDGET} não apareceu; visibilidade pode estar incorreta.`
    )
    return
  }
  elemento.style.visibility = visivel ? 'visible' : 'hidden'
}

// Quem já foi identificado nesta sessão de página, para não reenviar o login e mexer numa conversa
// em andamento. Só é gravado DEPOIS de a chamada acontecer de fato: marcar antes faria uma falha
// de disponibilidade do objeto do fornecedor virar "visitante anônimo" pelo resto da sessão.
let identidadeAplicada: string | null = null

/**
 * Identifica o usuário logado no chat, uma vez por identidade. Espera o objeto do fornecedor
 * aparecer — o `onload` do script garante que ele executou, não que o widget já esteja montado.
 * Trocar de usuário sem recarregar a página reidentifica normalmente.
 */
export const identificarNoChat = async (dados: DadosLoginChat) => {
  const identidade = `${dados.email}|${dados.name}`
  if (identidadeAplicada === identidade) return
  const widget = await esperar(() => window.movideskChatWidget)
  if (!widget) {
    // eslint-disable-next-line no-console
    console.warn(
      'Chat de suporte: widget indisponível; a conversa começará sem identificação.'
    )
    return
  }
  widget.login(dados)
  identidadeAplicada = identidade
}

/**
 * Encerra o uso do chat na sessão de página (logout). Oculta o widget e esquece a identidade, para
 * que o próximo usuário seja identificado de novo. O script em si não tem como ser descarregado —
 * hoje o login faz recarga completa da página, mas depender disso seria acoplamento frágil.
 */
export const resetarChat = () => {
  identidadeAplicada = null
  if (chatFoiCarregado()) {
    definirVisibilidadeChat(false)
  }
}
